import { useEffect, useState, useCallback } from "react";
import { api, forceLogout } from "../api/client";

export interface CurrentUser {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  tenant_id: string | null;
  tenant_name: string | null;
  email_verified: boolean;
  permissions?: Record<string, unknown> | null;
}

let cachedUser: CurrentUser | null = null;
let fetchPromise: Promise<CurrentUser | null> | null = null;

/** Fetch the current user from /auth/me. Returns null if not authenticated. */
export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const token = localStorage.getItem("lk_access");
  if (!token) {
    cachedUser = null;
    return null;
  }
  if (cachedUser) return cachedUser;
  if (fetchPromise) return fetchPromise;
  fetchPromise = api
    .get<CurrentUser>("/auth/me")
    .then((res) => {
      cachedUser = res.data;
      return res.data;
    })
    .catch((err) => {
      cachedUser = null;
      if (err?.response?.status === 401) forceLogout();
      return null;
    })
    .finally(() => {
      fetchPromise = null;
    });
  return fetchPromise;
}

/** Clear the cached user (call on logout). */
export function clearCurrentUser() {
  cachedUser = null;
}

/** React hook that provides the current user + loading state. */
export function useCurrentUser() {
  const [user, setUser] = useState<CurrentUser | null>(cachedUser);
  const [loading, setLoading] = useState(!cachedUser);

  const refresh = useCallback(async () => {
    setLoading(true);
    const u = await fetchCurrentUser();
    setUser(u);
    setLoading(false);
    return u;
  }, []);

  useEffect(() => {
    if (cachedUser) {
      setUser(cachedUser);
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  return { user, loading, refresh, setUser };
}
